import { Toaster } from "@designs-by-maryam/ui/components/sonner";
import type { QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
	createRootRouteWithContext,
	HeadContent,
	Outlet,
	Scripts,
	useRouterState,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import Header from "@/components/layout/header";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { FloatingWhatsApp } from "@/components/shared/floating-whatsapp";
import { NotFoundPage } from "@/components/shared/not-found";
import { RouteErrorComponent } from "@/components/shared/route-error-component";
import { AnimationProvider } from "@/lib/animation";
import { LanguageProvider } from "@/lib/language";
import { ThemeProvider, useTheme } from "@/lib/theme";
import type { orpc } from "@/utils/orpc";
import Footer from "../components/layout/footer";
import appCss from "../index.css?url";

function ThemedToaster() {
	const { resolvedTheme } = useTheme();
	return <Toaster richColors theme={resolvedTheme} />;
}
export type RouterAppContext = {
	orpc: typeof orpc;
	queryClient: QueryClient;
};

export const Route = createRootRouteWithContext<RouterAppContext>()({
	head: () => ({
		meta: [
			{
				charSet: "utf-8",
			},
			{
				name: "viewport",
				content: "width=device-width, initial-scale=1",
			},
			{
				title: "Designs by Maryam | Interior Design in Bahrain",
			},
		],
		links: [
			{
				rel: "stylesheet",
				href: appCss,
			},
			{
				rel: "icon",
				href: "/favicon.ico",
				type: "image/x-icon",
			},
			{
				rel: "icon",
				href: "/favicon.svg",
				type: "image/svg+xml",
			},
			{
				rel: "apple-touch-icon",
				href: "/apple-touch-icon.png",
			},
		],
	}),
	// shellComponent renders HTML structure on server, component renders app content
	// This pattern enables per-route SSR control via ssr: false on child routes
	shellComponent: RootShell,
	component: RootComponent,
	// Error handling: route-level error component for loader/render errors
	errorComponent: RouteErrorComponent,
	// 404 handling: shown when no route matches
	notFoundComponent: NotFoundPage,
});

// Inline script to prevent theme flash - reads cookies before React hydrates
// Also initializes color theme from saved hue preference
const themeScript = `
  (function() {
    // Dark/light theme
    var themeMatch = document.cookie.match(/theme-preference=([^;]+)/);
    var theme = themeMatch ? themeMatch[1] : 'dark';
    if (theme === 'system') {
      theme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    document.documentElement.classList.add(theme);

    // Color hue theme (OKLCH-based)
    var hueMatch = document.cookie.match(/color-hue=([^;]+)/);
    var hue = hueMatch ? parseInt(hueMatch[1], 10) : 175;
    if (isNaN(hue) || hue < 0 || hue > 360) hue = 175;

    // Generate and apply color variables based on hue
    var isDark = theme === 'dark';
    var root = document.documentElement;

    if (isDark) {
      root.style.setProperty('--primary', 'oklch(0.65 0.15 ' + hue + ')');
      root.style.setProperty('--primary-foreground', 'oklch(0.15 0.02 ' + hue + ')');
      root.style.setProperty('--secondary', 'oklch(0.25 0.03 ' + hue + ')');
      root.style.setProperty('--secondary-foreground', 'oklch(0.95 0.02 ' + hue + ')');
      root.style.setProperty('--accent', 'oklch(0.3 0.05 ' + hue + ')');
      root.style.setProperty('--accent-foreground', 'oklch(0.95 0.02 ' + hue + ')');
      root.style.setProperty('--ring', 'oklch(0.55 0.14 ' + hue + ')');
    } else {
      root.style.setProperty('--primary', 'oklch(0.45 0.13 ' + hue + ')');
      root.style.setProperty('--primary-foreground', 'oklch(0.99 0.01 ' + hue + ')');
      root.style.setProperty('--secondary', 'oklch(0.95 0.02 ' + hue + ')');
      root.style.setProperty('--secondary-foreground', 'oklch(0.25 0.05 ' + hue + ')');
      root.style.setProperty('--accent', 'oklch(0.93 0.05 ' + hue + ')');
      root.style.setProperty('--accent-foreground', 'oklch(0.25 0.08 ' + hue + ')');
      root.style.setProperty('--ring', 'oklch(0.55 0.14 ' + hue + ')');
    }
  })();
`;

/**
 * RootShell - HTML structure rendered on server
 * This component wraps the entire app and always executes on the server.
 * It handles the HTML document structure, head content, and Scripts.
 * Child routes can opt out of SSR with ssr: false while shell still renders.
 */
function RootShell({ children }: { children: React.ReactNode }) {
	return (
		<html lang="en">
			<head>
				<HeadContent />
				{/* biome-ignore lint/security/noDangerouslySetInnerHtml: Required for inline theme script to prevent FOUC */}
				<script dangerouslySetInnerHTML={{ __html: themeScript }} />
			</head>
			<body>
				{children}
				<Scripts />
			</body>
		</html>
	);
}

/**
 * RootComponent - App content rendered inside the shell
 * Contains providers, layout logic, and actual app UI.
 * Can have per-route SSR control via child route ssr options.
 */
function RootComponent() {
	// Check if we're in the dashboard layout (admin, designer, portal routes)
	// Note: Pathless layouts like _dashboard don't appear in location.pathname
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const isDashboard =
		pathname.startsWith("/admin") ||
		pathname.startsWith("/designer") ||
		pathname.startsWith("/portal");

	return (
		<ErrorBoundary>
			<ThemeProvider>
				<AnimationProvider>
					<LanguageProvider>
						{/* Skip to main content link for keyboard/screen reader users (WCAG 2.4.1) */}
						<a
							className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground focus:shadow-lg"
							href="#main-content"
						>
							Skip to main content
						</a>
						<div
							className={
								isDashboard
									? "min-h-svh"
									: "grid min-h-svh grid-rows-[auto_1fr_auto]"
							}
						>
							{!isDashboard && <Header />}
							<main id="main-content" className="isolate pt-20">
								<Outlet />
							</main>
							{!isDashboard && <Footer />}
							{!isDashboard && <FloatingWhatsApp />}
						</div>
						<ThemedToaster />
					</LanguageProvider>
				</AnimationProvider>
			</ThemeProvider>
			{import.meta.env.DEV && (
				<>
					<TanStackRouterDevtools position="bottom-left" />
					<ReactQueryDevtools buttonPosition="bottom-right" position="bottom" />
				</>
			)}
		</ErrorBoundary>
	);
}
